import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { RestaurantIdentity, RestaurantConfiguration } from '@/types';
import { DAY_KEYS, type Closure } from '@/types/restaurant';
import { InstagramIcon, TikTokIcon } from '@/components/ui/SocialIcons';
import { useStyleMode } from '@/features/restaurant/useStyleMode';
import { useFxEnabled, setFxEnabled } from '@/components/fx/fxPreference';
import { localeTag } from '@/lib/localeTag';
import { restaurantTz } from '@/lib/hours';
import { formatDisplayAddress, buildMapEmbedUrl } from '@/utils/restaurantAddress';

function parseDateUTC(iso: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return new Date(NaN);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

function closureWhen(c: Closure, t: TFunction, fmt: Intl.DateTimeFormat): { when: string; suffix: string } {
  const suffix = c.label ? ` — ${c.label}` : '';
  const when = c.from === c.to
    ? t('footer.dateOn', { date: fmt.format(parseDateUTC(c.from)) })
    : t('footer.dateRange', { from: fmt.format(parseDateUTC(c.from)), to: fmt.format(parseDateUTC(c.to)) });
  return { when, suffix };
}

function telHref(phone: string | null | undefined): string | undefined {
  if (!phone) return undefined;
  const cleaned = phone.replace(/[^\d+]/g, '');
  return cleaned ? `tel:${cleaned}` : undefined;
}

function mapsHref(address: string | null | undefined): string | undefined {
  if (!address) return undefined;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

// JS Date.getDay() is 0=Sunday..6=Saturday; map it to our Monday-first DayKey
// so we can highlight the current day's row (mirrors ouitap-showcase's todayKey()).
const WEEKDAY_BY_JS_DAY = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

interface Props { identity: RestaurantIdentity; config: RestaurantConfiguration; }

export default function SiteFooter({ identity, config: _config }: Props) {
  const { t, i18n } = useTranslation();
  const dateFmt = new Intl.DateTimeFormat(localeTag(i18n.language), {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
  const tel = telHref(identity.phone);
  // Rue + code postal + ville, composés ici comme sur la vitrine : `address`
  // seul perdait « 33400 Talence » chez les restaurants qui suivent la
  // convention du back-office (DF-0132).
  const address = formatDisplayAddress({
    address: identity.address,
    postalCode: identity.postalCode,
    city: identity.city,
  });
  const maps = mapsHref(address);
  // Carte intégrée : point d'entrée public « output=embed » de maps.google.com,
  // sans clé et donc sans consommer de quota Google Maps Platform (l'API Embed
  // et l'API JavaScript, elles, facturent chaque chargement).
  const mapEmbed = buildMapEmbedUrl(address, localeTag(i18n.language));
  // Footer text colour = `--on-footer` (applyTheme, DF-0424): the mode's
  // intended ink (premium `--ink`, playful `--paper`) unless it would vanish
  // on the theme's gradient. The fallbacks only apply before any theme loads.
  const premium = useStyleMode() === 'premium';
  const fxOn = useFxEnabled();

  // DF-0330 — "today"/"the current weekday" must read as the RESTAURANT sees
  // them, not the browser's timezone (a DOM restaurant's `country` still
  // reads "FR" while its hours run several hours off Paris).
  const tz = restaurantTz(identity);
  const todayIso = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  // Noon-UTC anchor on the restaurant-local calendar date: getUTCDay() then
  // reads the weekday of that date without depending on any timezone.
  const todayKey = WEEKDAY_BY_JS_DAY[new Date(`${todayIso}T12:00:00Z`).getUTCDay()];
  const futureClosures = (identity.closures ?? [])
    .filter((c) => c.to >= todayIso)
    .sort((a, b) => a.from.localeCompare(b.from))
    .slice(0, 3);
  const futureReservationClosures = (identity.reservationClosures ?? [])
    .filter((c) => c.to >= todayIso)
    .sort((a, b) => a.from.localeCompare(b.from))
    .slice(0, 3);

  return (
    // Opera premium swaps the techy monospace footer body for a clean sans;
    // headings keep their own font-display (serif) class. Playful stays mono.
    <footer className={premium ? 'font-sans' : 'font-mono'}>
      <div className="h-1 bg-mint-teal" aria-hidden="true" />

      <div
        className="relative px-6 py-14 md:py-16 overflow-hidden isolate"
        style={{
          background: 'linear-gradient(180deg, var(--purple) 0%, var(--magenta) 100%)',
          color: premium ? 'var(--on-footer, var(--ink))' : 'var(--on-footer, var(--paper))',
        }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full opacity-40 blur-3xl -z-10"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.55), transparent 65%)' }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-40 -right-24 w-[560px] h-[560px] rounded-full opacity-35 blur-3xl -z-10"
          style={{ background: 'radial-gradient(circle, rgba(0,0,0,0.45), transparent 65%)' }}
        />

        <div className="max-w-[1200px] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          <div className="lg:pr-2">
            <h3 className="font-display text-[26px] md:text-[30px] leading-[1.05] m-0 drop-shadow-[0_2px_0_rgba(0,0,0,0.18)]">
              {identity.name}
            </h3>
            <div className="mt-3 h-[2px] w-12 bg-mint-teal rounded-full" aria-hidden="true" />
            {address && (
              <a
                href={maps}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-5 inline-flex items-start gap-2 text-[13px] leading-[1.6] opacity-85 hover:opacity-100 transition-opacity"
              >
                <span className="mt-[2px] text-mint-teal" aria-hidden="true">📍</span>
                <span className="whitespace-pre-line border-b border-transparent group-hover:border-mint-teal/70 transition-colors">
                  {address}
                </span>
              </a>
            )}
            {mapEmbed && (
              <iframe
                src={mapEmbed}
                title={t('footer.mapTitle', { name: identity.name })}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="mt-4 block w-full max-w-[360px] aspect-[4/3] rounded-2xl border-0 ring-1 ring-paper/25 shadow-[0_6px_18px_rgba(0,0,0,0.18)]"
              />
            )}
          </div>

          <div>
            <h4 className="font-display text-mint-teal text-[18px] md:text-[22px] m-0 leading-none">
              {t('footer.hours')}
            </h4>
            <div className="mt-3 h-[2px] w-12 bg-mint-teal rounded-full" aria-hidden="true" />
            {futureClosures.length > 0 && (
              <ul className="mt-4 mb-2 list-none p-0 m-0 text-[12px] leading-[1.55] opacity-85 flex flex-col gap-1">
                {futureClosures.map((c) => (
                  <li key={`${c.from}-${c.to}`} className="flex items-center gap-2">
                    <span aria-hidden="true">🌴</span>
                    <span>{t('footer.closed', closureWhen(c, t, dateFmt))}</span>
                  </li>
                ))}
              </ul>
            )}
            {futureReservationClosures.length > 0 && (
              <ul className="mt-2 mb-4 list-none p-0 m-0 text-[12px] leading-[1.55] opacity-85 flex flex-col gap-1">
                {futureReservationClosures.map((c) => (
                  <li key={`r-${c.from}-${c.to}`} className="flex items-center gap-2">
                    <span aria-hidden="true">📵</span>
                    <span>{t('footer.reservationsClosed', closureWhen(c, t, dateFmt))}</span>
                  </li>
                ))}
              </ul>
            )}
            <ul className="mt-4 list-none p-0 m-0 text-[13px] leading-[1.55] flex flex-col">
              {DAY_KEYS.map((day) => {
                const ranges = identity.hours[day];
                const isToday = day === todayKey;
                return (
                  <li
                    key={day}
                    aria-current={isToday ? 'date' : undefined}
                    className={
                      'flex items-start justify-between gap-x-3 py-1.5 border-b border-paper/15 last:border-b-0' +
                      (isToday ? ' rounded-r-md bg-paper/10 pl-2.5' : ' opacity-85')
                    }
                    style={isToday ? { boxShadow: 'inset 3px 0 0 var(--mint-teal)' } : undefined}
                  >
                    <span className="flex min-w-0 items-center gap-2 font-bold">
                      {t(`weekdays.${day}`)}
                      {isToday && (
                        <span className="shrink-0 rounded-full border border-mint-teal/50 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wide text-mint-teal">
                          {t('footer.today')}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-right tabular-nums">
                      {!ranges || ranges.length === 0 ? (
                        t('common.closed')
                      ) : (
                        ranges.map((r, i) => (
                          <span key={i} className="block whitespace-nowrap">
                            {`${r.open}–${r.close}`}
                          </span>
                        ))
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-mint-teal text-[18px] md:text-[22px] m-0 leading-none">
              {t('footer.contact')}
            </h4>
            <div className="mt-3 h-[2px] w-12 bg-mint-teal rounded-full" aria-hidden="true" />
            {identity.phone && (
              <a
                href={tel}
                className="group mt-4 inline-flex items-center gap-2 text-[15px] leading-[1.4] hover:text-mint-teal transition-colors"
              >
                <span className="text-mint-teal" aria-hidden="true">📞</span>
                <span className="font-bold tracking-wide border-b border-transparent group-hover:border-mint-teal transition-colors">
                  {identity.phone}
                </span>
              </a>
            )}
            <p className="mt-3 text-[12px] leading-[1.5] opacity-65 m-0">
              {t('footer.clickToCall')}
            </p>
          </div>

          <div>
            <h4 className="font-display text-mint-teal text-[18px] md:text-[22px] m-0 leading-none">
              {t('footer.followUs')}
            </h4>
            <div className="mt-3 h-[2px] w-12 bg-mint-teal rounded-full" aria-hidden="true" />
            <div className="mt-4 flex gap-3 items-center">
              {identity.instagramLink && (
                <a
                  href={identity.instagramLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-paper/10 ring-1 ring-paper/25 backdrop-blur-sm transition-all hover:bg-paper/20 hover:ring-mint-teal hover:-translate-y-[2px] hover:scale-105"
                >
                  <InstagramIcon className="w-6 h-6" />
                </a>
              )}
              {identity.tiktokLink && (
                <a
                  href={identity.tiktokLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                  className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-paper/10 ring-1 ring-paper/25 backdrop-blur-sm transition-all hover:bg-paper/20 hover:ring-mint-teal hover:-translate-y-[2px] hover:scale-105"
                >
                  <TikTokIcon className="w-6 h-6" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-mint-teal border-t-2 border-ink px-6 py-3 text-center text-sm text-[var(--on-mint-teal)]">
        © {new Date().getFullYear()} {identity.name} · {t('footer.copyright')}
        {/* Guest-side switch for the animated decoration (cursor trail,
            onomatopoeia, petals…). Stored in this browser only. */}
        <span className="block mt-1 text-[11px]">
          <button
            type="button"
            onClick={() => setFxEnabled(!fxOn)}
            aria-pressed={fxOn}
            // DF-0425 — preflight is off on this site: a bare <button> keeps the
            // UA grey fill and outset border, a grey block on the band.
            className="bg-transparent border-0 p-0 text-inherit [font:inherit] cursor-pointer hover:underline"
          >
            {fxOn ? t('footer.animationsOn') : t('footer.animationsOff')}
          </button>
        </span>
        {/* Platform signature. Deliberately text-only: this band wears the
            restaurant's own accent colour, which is arbitrary, and a coloured
            OuiTAP mark dropped onto it would clash with the very brand the page
            exists to showcase. Small type so it never competes with it — no
            opacity: at 70 % it cost Yuji's band 7.2:1 → 4.2:1 (DF-0426). */}
        <span className="block mt-1 text-[11px]">
          <a
            href="https://ouitap.fr"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            {t('footer.poweredBy')}
          </a>
        </span>
      </div>
    </footer>
  );
}
